import React, { useCallback, useEffect, useMemo, useState } from "react";
import AccessibleDatePicker from "./AccessibleDatePicker";
import AccessibleTimePicker from "./AccessibleTimePicker";
import BaseSelect from "../../Base/BaseSelect";
import useLocationsService from "../../../services/useLocationsService";

export type ServiceClonePayload = {
  service_date: string;
  service_date_to?: string;
  service_time: string;
  change_destination?: boolean;
  destination_id?: number;
  change_origin?: boolean;
  origin_id?: number;
};

export const DUPLICATE_MAX_RANGE_DAYS = 31;

interface ServiceCloneFormFieldsProps {
  serviceDate: string;
  serviceTime: string;
  minDateYmd: string;
  onDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Modo duplicar: checkbox "cambiar destino". */
  changeDestination?: boolean;
  onChangeDestination?: (checked: boolean) => void;
  destinationId: string;
  onDestinationChange: (value: string) => void;
  /** Duplicar en rango: fecha hasta + toggle varios días. */
  allowDateRange?: boolean;
  multiDay?: boolean;
  onMultiDayChange?: (checked: boolean) => void;
  serviceDateTo?: string;
  onDateToChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  rangeDayCount?: number;
  /** Modo retorno / modificar ruta: origen + destino + invertir. */
  fullRoute?: boolean;
  originId?: string;
  onOriginChange?: (value: string) => void;
  onSwapRoute?: () => void;
  locationOptions: Array<{ value: string; label: string }>;
  loadingLocations?: boolean;
  currentDestinationLabel?: string | null;
  currentOriginLabel?: string | null;
  /** Prefijo del resumen de ruta (ej. "Ruta actual", "Retorno por defecto"). */
  routeHintLabel?: string;
  dateFieldId: string;
  timeFieldId: string;
}

const ServiceCloneFormFields: React.FC<ServiceCloneFormFieldsProps> = ({
  serviceDate,
  serviceTime,
  minDateYmd,
  onDateChange,
  onTimeChange,
  changeDestination = false,
  onChangeDestination,
  destinationId,
  onDestinationChange,
  allowDateRange = false,
  multiDay = false,
  onMultiDayChange,
  serviceDateTo = "",
  onDateToChange,
  rangeDayCount = 1,
  fullRoute = false,
  originId = "",
  onOriginChange,
  onSwapRoute,
  locationOptions,
  loadingLocations = false,
  currentDestinationLabel,
  currentOriginLabel,
  routeHintLabel = "Ruta",
  dateFieldId,
  timeFieldId,
}) => {
  const isPastDate = Boolean(serviceDate) && serviceDate < minDateYmd;
  const rangeInvalid =
    multiDay &&
    Boolean(serviceDate) &&
    Boolean(serviceDateTo) &&
    serviceDateTo < serviceDate;
  const rangeTooLong = multiDay && rangeDayCount > DUPLICATE_MAX_RANGE_DAYS;

  return (
    <div className="service-duplicate-fields">
      {allowDateRange && (
        <label className="service-duplicate-toggle">
          <input
            type="checkbox"
            checked={multiDay}
            onChange={(e) => onMultiDayChange?.(e.target.checked)}
          />
          <span>Duplicar en varios días</span>
        </label>
      )}

      <AccessibleDatePicker
        id={dateFieldId}
        name="service_date"
        label={multiDay && allowDateRange ? "Desde" : "Fecha del servicio"}
        value={serviceDate}
        onChange={onDateChange}
        minDateYmd={minDateYmd}
      />
      {isPastDate && (
        <p className="service-duplicate-error" role="alert">
          La fecha no es válida. Solo puedes programar servicios desde el {minDateYmd} en
          adelante.
        </p>
      )}

      {allowDateRange && multiDay && (
        <>
          <AccessibleDatePicker
            id={`${dateFieldId}_to`}
            name="service_date_to"
            label="Hasta"
            value={serviceDateTo}
            onChange={onDateToChange!}
            minDateYmd={serviceDate || minDateYmd}
          />
          {rangeInvalid && (
            <p className="service-duplicate-error" role="alert">
              La fecha hasta no puede ser anterior a la fecha desde.
            </p>
          )}
          {rangeTooLong && (
            <p className="service-duplicate-error" role="alert">
              El rango no puede superar {DUPLICATE_MAX_RANGE_DAYS} días.
            </p>
          )}
          {!rangeInvalid && !rangeTooLong && rangeDayCount > 0 && (
            <p className="service-duplicate-range-summary" role="status">
              Se crearán <strong>{rangeDayCount}</strong> servicio
              {rangeDayCount !== 1 ? "s" : ""} (misma hora cada día)
            </p>
          )}
        </>
      )}

      <AccessibleTimePicker
        id={timeFieldId}
        name="service_time"
        label="Hora del servicio"
        value={serviceTime}
        onChange={onTimeChange}
      />

      {fullRoute ? (
        <>
          {(currentOriginLabel || currentDestinationLabel) && (
            <p className="service-duplicate-hint">
              {routeHintLabel}:{" "}
              <strong>{currentOriginLabel || "—"}</strong>
              {" → "}
              <strong>{currentDestinationLabel || "—"}</strong>
            </p>
          )}
          <BaseSelect
            label="Origen"
            name="origin_id"
            placeholder={loadingLocations ? "Cargando ubicaciones…" : "Seleccionar origen"}
            preserveCase
            searchable
            disabled={loadingLocations}
            options={locationOptions}
            value={originId}
            onChange={(e) => onOriginChange?.(e.target.value)}
          />
          <div className="service-duplicate-swap-row">
            <button
              type="button"
              className="service-duplicate-swap-btn"
              onClick={onSwapRoute}
              title="Intercambiar origen y destino"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l4-4M17 20l-4-4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Invertir
            </button>
          </div>
          <BaseSelect
            label="Destino"
            name="destination_id"
            placeholder={loadingLocations ? "Cargando ubicaciones…" : "Seleccionar destino"}
            preserveCase
            searchable
            disabled={loadingLocations}
            options={locationOptions}
            value={destinationId}
            onChange={(e) => onDestinationChange(e.target.value)}
          />
        </>
      ) : (
        <>
          <label className="service-duplicate-toggle">
            <input
              type="checkbox"
              checked={changeDestination}
              onChange={(e) => onChangeDestination?.(e.target.checked)}
            />
            <span>¿Cambiar destino?</span>
          </label>

          {currentDestinationLabel && !changeDestination && (
            <p className="service-duplicate-hint">
              Destino actual: <strong>{currentDestinationLabel}</strong>
            </p>
          )}

          {changeDestination && (
            <BaseSelect
              label="Nuevo destino"
              name="destination_id"
              placeholder={loadingLocations ? "Cargando ubicaciones…" : "Seleccionar destino"}
              preserveCase
              searchable
              disabled={loadingLocations}
              options={locationOptions}
              value={destinationId}
              onChange={(e) => onDestinationChange(e.target.value)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ServiceCloneFormFields;

/** Carga el catálogo de ubicaciones para los modales de duplicar/retorno. */
export function useServiceCloneLocations(isOpen: boolean) {
  const { list, fetching } = useLocationsService();
  const [locations, setLocations] = useState<Array<{ id: number; name: string }>>([]);

  const loadLocations = useCallback(() => {
    list(
      { all: "true" },
      {
        onSuccess: (resp: any) => {
          const data = resp?.data ?? resp ?? {};
          const items = Array.isArray(data.locations) ? data.locations : [];
          setLocations(
            items
              .filter((l: any) => l?.id != null && l?.name)
              .map((l: any) => ({ id: Number(l.id), name: String(l.name) }))
          );
        },
      }
    );
  }, [list]);

  useEffect(() => {
    if (isOpen) loadLocations();
  }, [isOpen, loadLocations]);

  const locationOptions = useMemo(
    () =>
      [...locations]
        .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }))
        .map((l) => ({ value: String(l.id), label: l.name })),
    [locations]
  );

  return {
    locationOptions,
    loadingLocations: Boolean(fetching.list),
    reloadLocations: loadLocations,
  };
}
